import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryState,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
} from '@urbanx/agx.activities.client';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';
import { TaskModalPage } from './taskModalPageStatus';

type ResolvedTask = AutopilotTaskDto & { id: string };

interface AgreementComplianceLayoutDefinition {
  state: TaskCategoryState;
  stepCount: number;
  pageStepOrders: number[][];
}

const agreementComplianceLayouts: Partial<
  Record<CampaignDetailModelState, AgreementComplianceLayoutDefinition>
> = {
  [CampaignDetailModelState.QLD]: {
    state: TaskCategoryState.QLD,
    stepCount: 10,
    pageStepOrders: [[1, 2, 3, 4, 5, 6], [7, 8, 9], [10]],
  },
  [CampaignDetailModelState.NSW]: {
    state: TaskCategoryState.NSW,
    stepCount: 12,
    pageStepOrders: [
      [1, 2, 3, 4, 5, 6, 7, 8, 9],
      [10, 11, 12],
    ],
  },
};

export const getInteractiveAgreementComplianceTask = (tasks: AutopilotTaskDto[]) => {
  const complianceTasks = tasks.filter(
    (task) =>
      !task.isLocked &&
      task.category?.workflowType === TaskCategoryWorkflowType.Campaign &&
      task.category?.stage === TaskCategoryStage.AgreementCompliance &&
      task.category.taskType === TaskCategoryTaskType.AgreementCompliance
  );

  if (complianceTasks.length > 1) {
    throw new Error(
      `Agreement Compliance requires one canonical task; received ${complianceTasks.length}`
    );
  }

  return complianceTasks[0];
};

const layoutForState = (
  state: CampaignDetailModelState | undefined
): AgreementComplianceLayoutDefinition => {
  if (!state) throw new Error('Agreement Compliance state is missing');

  const layout = agreementComplianceLayouts[state];
  if (!layout) throw new Error(`Agreement Compliance layout is not supported for ${state}`);

  return layout;
};

const requireCanonicalTask = (
  tasks: AutopilotTaskDto[],
  layout: AgreementComplianceLayoutDefinition
): ResolvedTask => {
  if (tasks.length !== 1) {
    throw new Error(
      `Agreement Compliance requires exactly one canonical task; received ${tasks.length}`
    );
  }

  const task = tasks[0];
  if (!task?.id) throw new Error('Agreement Compliance task is missing an ID');
  if (
    task.category?.workflowType !== TaskCategoryWorkflowType.Campaign ||
    task.category.stage !== TaskCategoryStage.AgreementCompliance ||
    task.category.taskType !== TaskCategoryTaskType.AgreementCompliance ||
    task.category.state !== layout.state
  ) {
    throw new Error(
      `Agreement Compliance task does not match the canonical ${layout.state} category`
    );
  }

  const steps = [...(task.taskSteps ?? [])].sort(
    (left, right) => (left.order ?? 0) - (right.order ?? 0)
  );
  if (steps.length !== layout.stepCount || steps.some((step, index) => step.order !== index + 1)) {
    throw new Error(
      `Agreement Compliance task does not match its ${layout.stepCount}-step canonical layout`
    );
  }

  return { ...task, taskSteps: steps } as ResolvedTask;
};

const stepAt = (task: ResolvedTask, order: number) => {
  const step = task.taskSteps?.[order - 1];
  if (!step || step.order !== order) {
    throw new Error(`Agreement Compliance step ${order} is missing`);
  }

  return step;
};

const pageFor = (task: ResolvedTask, stepOrders: number[], pageIndex: number): TaskModalPage => {
  const pageTask: ResolvedTask = {
    ...task,
    id: `${task.id}:agreement-compliance-page-${pageIndex + 1}`,
    taskSteps: stepOrders.map((order) => stepAt(task, order)),
  };

  return {
    task: pageTask,
    completionTasks: [pageTask],
    stepBindings: stepOrders.map((order) => ({
      pageStepOrder: order,
      taskId: task.id,
      taskStepOrder: order,
    })),
  };
};

const resolveLayout = (tasks: AutopilotTaskDto[], layout: AgreementComplianceLayoutDefinition) => {
  const task = requireCanonicalTask(tasks, layout);
  const pages = layout.pageStepOrders.map((orders, index) => pageFor(task, orders, index));

  return {
    pages,
    agreementDetailsPageTask: pages[0]!.task,
  };
};

export const createAgreementComplianceTaskModalFlow = (
  state: CampaignDetailModelState | undefined
) => {
  const layout = layoutForState(state);

  return {
    resolvePages: (tasks: AutopilotTaskDto[]) => resolveLayout(tasks, layout).pages,
    resolveAgreementDetailsPageTask: (tasks: AutopilotTaskDto[]) =>
      resolveLayout(tasks, layout).agreementDetailsPageTask,
  };
};
